export const MAX_FILE_MB = 100

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
const POINTS_PER_MM = 72 / 25.4
const MIN_CROPPED_SIDE_MM = 1

export interface CropMarginsMm {
  top: number
  right: number
  bottom: number
  left: number
}

export interface PageDimensions {
  width: number
  height: number
  widthMm: number
  heightMm: number
}

export interface CropInspection {
  pageCount: number
  firstPage: PageDimensions
  minPage: PageDimensions
}

export type CropValidationResult =
  | { ok: true }
  | { ok: false; code: 'empty' | 'negative' | 'too_large' }

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function croppedFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base}-cropped.pdf`
}

export function pointsToMm(points: number): number {
  return points / POINTS_PER_MM
}

export function mmToPoints(mm: number): number {
  return mm * POINTS_PER_MM
}

export function formatMm(mm: number): string {
  return mm >= 10 ? mm.toFixed(0) : mm.toFixed(1)
}

export function normalizeMargins(margins: CropMarginsMm): CropMarginsMm {
  return {
    top: Math.max(0, finiteOrZero(margins.top)),
    right: Math.max(0, finiteOrZero(margins.right)),
    bottom: Math.max(0, finiteOrZero(margins.bottom)),
    left: Math.max(0, finiteOrZero(margins.left)),
  }
}

export function validateCropMargins(
  margins: CropMarginsMm,
  inspection: CropInspection,
): CropValidationResult {
  const values = [margins.top, margins.right, margins.bottom, margins.left]
  if (values.some((value) => !Number.isFinite(value) || value < 0)) {
    return { ok: false, code: 'negative' }
  }
  if (values.every((value) => value === 0)) {
    return { ok: false, code: 'empty' }
  }

  if (
    margins.left + margins.right > inspection.minPage.widthMm - MIN_CROPPED_SIDE_MM ||
    margins.top + margins.bottom > inspection.minPage.heightMm - MIN_CROPPED_SIDE_MM
  ) {
    return { ok: false, code: 'too_large' }
  }

  return { ok: true }
}

export async function inspectPdfForCrop(file: File): Promise<CropInspection> {
  const { PDFDocument } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(bytes)
  const pages = pdfDoc.getPages()
  const firstPdfPage = pages[0]
  if (!firstPdfPage) {
    throw new Error('PDF has no pages')
  }
  const firstPage = dimensionsFromSize(firstPdfPage.getSize())

  const minPage = pages.reduce<PageDimensions>((min, page) => {
    const dimensions = dimensionsFromSize(page.getSize())
    return {
      width: Math.min(min.width, dimensions.width),
      height: Math.min(min.height, dimensions.height),
      widthMm: Math.min(min.widthMm, dimensions.widthMm),
      heightMm: Math.min(min.heightMm, dimensions.heightMm),
    }
  }, firstPage)

  return {
    pageCount: pages.length,
    firstPage,
    minPage,
  }
}

export async function cropPdf(
  file: File,
  marginsMm: CropMarginsMm,
): Promise<{ blob: Blob; pageCount: number }> {
  const { PDFDocument } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(bytes)
  const margins = normalizeMargins(marginsMm)

  for (const page of pdfDoc.getPages()) {
    const { width, height } = page.getSize()
    const left = mmToPoints(margins.left)
    const right = mmToPoints(margins.right)
    const top = mmToPoints(margins.top)
    const bottom = mmToPoints(margins.bottom)

    page.setCropBox(
      left,
      bottom,
      Math.max(1, width - left - right),
      Math.max(1, height - top - bottom),
    )
  }

  const out = await pdfDoc.save()
  return {
    blob: new Blob([out], { type: 'application/pdf' }),
    pageCount: pdfDoc.getPageCount(),
  }
}

function dimensionsFromSize(size: { width: number; height: number }): PageDimensions {
  return {
    width: size.width,
    height: size.height,
    widthMm: pointsToMm(size.width),
    heightMm: pointsToMm(size.height),
  }
}

function finiteOrZero(value: number): number {
  return Number.isFinite(value) ? value : 0
}
